import type { AnalyticsPlugin } from 'analytics'

function countOccurrences(str: string, char: string) {
    return str.split(char).length - 1
}

function formatError(event: string): (msg: string) => boolean {
    const errorMessage = [
        `> Invalid event "${event}"`,
        '> Formatting information:',
        'Event must match pattern "context:objectName:actionName"',
        'Event must be camelCased "camelCase:camelCase:camelCase"',
        'Example: widget:sites:deploySucceeded',
    ]

    return (msg: string) => {
        // eslint-disable-next-line no-console
        console.log(errorMessage.join('\n'))
        // eslint-disable-next-line no-console
        if (msg) console.log(msg)
        return false
    }
}

function isValidEventName(event: string, config: EventValidationConfig) {
    const validProject = [config.context]
    const validWidgetApps = config.widgetApps || []
    const invalid = formatError(event)

    const colonCount = countOccurrences(event, ':')
    if (colonCount !== 2) {
        return invalid(`Event must have two colons. "${event}" contains ${colonCount}`)
    }

    const matches = event.match(/([a-zA-Z]+):([a-zA-Z0-9]+):([a-zA-Z]+$)/)
    if (!matches) {
        return invalid('Event malformed')
    }

    const [, context, object, action] = matches

    // if missing any parts of event, exit;
    if (!context || !object || !action) {
        return invalid('Missing context, object, or action')
    }

    /* Validate project name */
    if (validProject.indexOf(context) === -1) {
        return invalid(`Invalid context "${context}". Must be 1 of ${JSON.stringify(validProject)}`)
    }

    /* Validate object */
    if (validWidgetApps.indexOf(object) === -1) {
        return invalid(`Invalid object "${object}". Must be 1 of ${JSON.stringify(validWidgetApps)}`)
    }
    return true
}

type EventValidationConfig = {
    [key: string]: unknown
    context: string
    widgetApps: string[]
    workspaceId: string
}

type TrackStart = {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    payload: any
    config: EventValidationConfig
    abort: (msg: string) => void
}

const ALLOWED_WORKSPACES = [
    '1214537e-18bf-4f73-9409-649bd44c9d35',
    '73df3231-4c30-4fbe-b42e-d0b122230787',
    '4976cda5-d75f-461c-b192-3c4b84cf60b0',
    'a6440cf2-3e36-482b-b36f-c3b182e57564',
    '79a8d150-d50d-4d2a-b51e-8f33c85e9587',
    '7706681b-d54b-44f1-859e-e750754f3a35',
    '49b7379a-86da-463f-bcc6-d0fd2854010a',
    'a94a4961-8555-4feb-bd60-32fee793de0d',
]

export function eventValidationPlugin(pluginConfig: EventValidationConfig): AnalyticsPlugin {
    return {
        name: 'event-validation',
        config: {
            throwOnInvalid: false,
            ...pluginConfig,
        },
        trackStart: ({ payload, config, abort }: TrackStart) => {
            if (!ALLOWED_WORKSPACES.includes(config.workspaceId)) {
                return abort('invalid workspace id - event aborted')
            }

            if (!isValidEventName(payload.event, config)) {
                const msg = `invalid event name ${payload.event}`
                if (config.throwOnInvalid) {
                    throw new Error(msg)
                }
                return abort(msg)
            }

            return null
        },
    }
}
